import React, { useState, useEffect } from 'react';
import { userMapsApi } from '../../services/userMapsApi';
import { projectsApi, Project } from '../../services/projectsApi';
import { describeRequestError } from '../../services/http';
import type { SavedCountryCode } from '../../config/catalogue';

interface SaveGeometriesProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
  selectedMapType: string;
  onSave: (name: string, description: string) => void;
  /**
   * The country the selected areas belong to, as it is saved: a registry code ('NOR') for the old maps, the
   * catalogue's code ('no') for the catalogue 1.0 maps (config/catalogue.ts). Every save and update sends it.
   */
  countryCode: SavedCountryCode;
  /** The saved area being edited: the dialog then updates it instead of creating a new one. */
  editArea?: { id: string; name: string; description?: string };
}

const SaveGeometries: React.FC<SaveGeometriesProps> = ({
  isOpen,
  onClose,
  selectedIds,
  selectedMapType,
  onSave,
  countryCode,
  editArea,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectChoice, setProjectChoice] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [showNameError, setShowNameError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedData, setSavedData] = useState<{ id: string; name: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const editId = editArea?.id;
  const editName = editArea?.name;
  const editDescription = editArea?.description;

  // Load projects so a new area can be added to one. An edit leaves its projects as they are.
  useEffect(() => {
    if (isOpen && !editId) {
      projectsApi.list().then(setProjects).catch(() => {});
    }
  }, [isOpen, editId]);

  // Editing starts from the saved name and description.
  useEffect(() => {
    if (isOpen && editId) {
      setName(editName ?? '');
      setDescription(editDescription ?? '');
    }
  }, [isOpen, editId, editName, editDescription]);

  const handleContinue = async () => {
    if (!name.trim()) {
      setShowNameError(true);
      return;
    }

    setIsLoading(true);
    setSaveError(null);
    try {
      const body = {
        name,
        description,
        type: selectedMapType,
        ids: selectedIds,
        country_code: countryCode,
      };
      const result = editArea
        ? await userMapsApi.updateGeometries(editArea.id, body)
        : await userMapsApi.saveGeometries(body);

      setSavedData({ id: result.id ?? editArea?.id ?? '', name: result.name ?? name });
      setShowSuccess(true);
      onSave(name, description);

      // Optionally link a newly saved area to a project.
      if (!editArea && result.id && projectChoice) {
        try {
          if (projectChoice === '__new__' && newProjectName.trim()) {
            await projectsApi.create({ name: newProjectName.trim(), areaIds: [result.id] });
          } else if (projectChoice !== '__new__') {
            await projectsApi.addAreas(projectChoice, [result.id]);
          }
        } catch (err) {
          console.error('Failed to link to project:', err);
        }
      }

      // Auto-close after showing success for 2 seconds
      setTimeout(() => {
        setName('');
        setDescription('');
        setShowNameError(false);
        setShowSuccess(false);
        setSavedData(null);
        setProjectChoice('');
        setNewProjectName('');
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to save geometries:', error);
      setSaveError(describeRequestError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setSaveError(null);
    setShowNameError(false);
    setShowSuccess(false);
    setSavedData(null);
    setProjectChoice('');
    setNewProjectName('');
    onClose();
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (showNameError && e.target.value.trim()) {
      setShowNameError(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        {showSuccess ? (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-success-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold text-success mb-2">{editArea ? 'Changes saved.' : 'Area saved.'}</h2>
            <p className="text-base-content/70 mb-2">{savedData?.name}</p>
            <p className="text-sm text-base-content/50">ID: {savedData?.id}</p>
            <p className="text-xs text-base-content/40 mt-4">Closing automatically...</p>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-bold mb-4">{editArea ? 'Update area' : 'Save area'}</h2>
        
        <div className="form-control w-full mb-4">
          <label className="label">
            <span className="label-text">Name *</span>
          </label>
          <input
            type="text"
            placeholder="Enter geometry set name"
            className={`input input-bordered w-full ${showNameError ? 'input-error' : ''}`}
            value={name}
            onChange={handleNameChange}
          />
          {showNameError && (
            <label className="label">
              <span className="label-text-alt text-error">Please enter a name</span>
            </label>
          )}
        </div>

        <div className="form-control w-full mb-4">
          <label className="label">
            <span className="label-text">Description</span>
          </label>
          <textarea
            className="textarea textarea-bordered h-24"
            placeholder="Enter description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {!editArea && (
        <div className="form-control w-full mb-4">
          <label className="label">
            <span className="label-text">Add to project</span>
          </label>
          <select
            className="select select-bordered w-full"
            value={projectChoice}
            onChange={(e) => setProjectChoice(e.target.value)}
          >
            <option value="">No project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            <option value="__new__">+ New project…</option>
          </select>
          {projectChoice === '__new__' && (
            <input
              type="text"
              placeholder="New project name"
              className="input input-bordered w-full mt-2"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
            />
          )}
        </div>
        )}

        <div className="form-control w-full mb-6">
          <label className="label">
            <span className="label-text">Selected IDs ({selectedIds.length})</span>
          </label>
          <div className="max-h-32 overflow-y-auto bg-base-200 rounded p-3">
            {selectedIds.length === 0 ? (
              <div className="text-sm text-base-content/60">No items selected</div>
            ) : (
              <div className="space-y-1">
                {selectedIds.map((id) => (
                  <div key={id} className="text-sm font-mono bg-base-100 px-2 py-1 rounded">
                    {id}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {saveError && (
          <div role="alert" className="alert alert-error text-sm mb-4">
            Couldn't save the area. {saveError}
          </div>
        )}

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={handleClose}>
            Cancel
          </button>
          <button
            className={`btn btn-primary ${!name.trim() || isLoading ? 'btn-disabled' : ''}`}
            onClick={handleContinue}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Saving...
              </>
            ) : (
              editArea ? 'Save changes' : 'Save area'
            )}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default SaveGeometries;